import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { UsersService } from '../../services/users.service';
import { MessageService } from 'base';
import { GoogleWorkspaceSettingsDto, AccountType, LoginResultDto, LoginResultCode }
	from 'UvExplorerServer.Dtos.TS/Auth';
import { List } from 'DiscoveryEngine.TS/Utils';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { IdentityService } from 'identity';
import { SessionStateEntryDto } from '../../../UvExplorerServer.Dtos.TS/Session';
import { ComponentBase } from '../../../common';


@Component({
	selector: 'gw-auth-settings',
	templateUrl: './gw-auth-settings.component.html',
	styleUrls: ['./gw-auth-settings.component.scss']
})
export class GwAuthSettingsComponent extends ComponentBase implements OnInit, OnDestroy, OnChanges {

	public form: UntypedFormGroup;

	public gwEnabled: UntypedFormControl;
	public gwDomainNames: UntypedFormControl;
	public gwQueryPageSize: UntypedFormControl;
	public gwAutoSyncDirectoryDataEnabled: UntypedFormControl;
	public gwAutoSyncDirectoryDataDays: UntypedFormControl;
	public gwAutoSyncDirectoryDataHours: UntypedFormControl;
	public gwAutoSyncDirectoryDataMinutes: UntypedFormControl;

	private gwSettingsLoadedSub: Subscription;
	private gwSettings: GoogleWorkspaceSettingsDto;

	public gwAdminDialogVisible: boolean;

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService,
		private identityService: IdentityService,
		private messageService: MessageService) {

		super();

		this.gwSettings = null;

		this.resetGwAdminDialogState();

		this.gwSettingsLoadedSub = this.usersService.onGoogleWorkspaceSettingsLoaded.subscribe(
			(result: GoogleWorkspaceSettingsDto) => {
				this.copyGwSettings();
			});
	}

	private resetGwAdminDialogState(): void {
		this.gwAdminDialogVisible = false;
	}

	private copyGwSettings(): void {
		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.gwSettings = new GoogleWorkspaceSettingsDto();
			ObjSerUtils.Copy(this.usersService.gwSettings, this.gwSettings);
		}
		else {
			this.gwSettings = null;

			const errorMessage = "Could not retrieve authentication settings";
			this.messageService.displayErrorMessage(errorMessage);
		}

		this.buildForm();
	}

	public ngOnInit(): void {

		this.buildForm();

		if (this.usersService.hasGoogleWorkspaceSettings) {
			this.copyGwSettings();
		}
	}

	public ngOnDestroy(): void {
		if (this.gwSettingsLoadedSub) {
			this.gwSettingsLoadedSub.unsubscribe();
			this.gwSettingsLoadedSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {

	}

	private buildForm(): void {
		let gwEnabledValue = false;
		let gwDomainNamesValue = "";
		let gwQueryPageSizeValue = 0;
		let gwAutoSyncDirectoryDataEnabled = false;
		let gwAutoSyncDirectoryDataDays = 0;
		let gwAutoSyncDirectoryDataHours = 0;
		let gwAutoSyncDirectoryDataMinutes = 0;

		if (!!this.gwSettings) {
			gwEnabledValue = this.gwSettings.Enabled;
			gwDomainNamesValue = this.domainNamesToString(this.gwSettings.DomainNames);
			gwQueryPageSizeValue = this.gwSettings.QueryPageSize;
			gwAutoSyncDirectoryDataEnabled = this.gwSettings.AutoSyncDirectoryData_Enabled;
			gwAutoSyncDirectoryDataDays = this.gwSettings.AutoSyncDirectoryData_Days;
			gwAutoSyncDirectoryDataHours = this.gwSettings.AutoSyncDirectoryData_Hours;
			gwAutoSyncDirectoryDataMinutes = this.gwSettings.AutoSyncDirectoryData_Minutes;
		}

		this.gwEnabled = new UntypedFormControl(gwEnabledValue);
		this.gwDomainNames = new UntypedFormControl(gwDomainNamesValue,
			this.requiredIf(() => this.gwEnabled.value, "gwDomainNamesRequired"));
		this.gwQueryPageSize = new UntypedFormControl(gwQueryPageSizeValue, Validators.compose([
			this.minValue(1), this.maxValue(200)
		]));
		this.gwAutoSyncDirectoryDataEnabled = new UntypedFormControl(gwAutoSyncDirectoryDataEnabled);
		this.gwAutoSyncDirectoryDataDays = new UntypedFormControl(gwAutoSyncDirectoryDataDays,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(30)]));
		this.gwAutoSyncDirectoryDataHours = new UntypedFormControl(gwAutoSyncDirectoryDataHours,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(23)]));
		this.gwAutoSyncDirectoryDataMinutes = new UntypedFormControl(gwAutoSyncDirectoryDataMinutes,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(59)]));

		this.enableDisable();

		this.form = this.fb.group({
			gwEnabled: this.gwEnabled,
			gwDomainNames: this.gwDomainNames,
			gwQueryPageSize: this.gwQueryPageSize,
			gwAutoSyncDirectoryDataEnabled: this.gwAutoSyncDirectoryDataEnabled,
			gwAutoSyncDirectoryDataDays: this.gwAutoSyncDirectoryDataDays,
			gwAutoSyncDirectoryDataHours: this.gwAutoSyncDirectoryDataHours,
			gwAutoSyncDirectoryDataMinutes: this.gwAutoSyncDirectoryDataMinutes,
		},
		{
			validator: this.validAutoSyncSettings()
		});

		setTimeout(() => this.enableDisable(), 0);
	}

	private validAutoSyncSettings(): ValidatorFn {
		return () => {
			if (!this.gwAutoSyncDirectoryDataEnabled.value) {
				return null;
			}

			if (this.gwAutoSyncDirectoryDataDays.value > 0 ||
				this.gwAutoSyncDirectoryDataHours.value > 0 ||
				this.gwAutoSyncDirectoryDataMinutes.value >= 15) {

				return null;
			}

			return {
				nonEmptyAutoSyncSettings: { valid: false }
			};
		};
	}

	private domainNamesToString(domainNames: List<string>) {
		let result = "";

		domainNames.ForEach((dn: string) => {
			result += (dn + '\n');
		});

		return result;
	}

	private stringToDomainNames(str: string): List<string> {
		let result = new List<string>();

		let domainNames = str.split(/\s+/);
		domainNames.forEach((v: string) => {
			if (v) {
				result.Add(v);
			}
		});

		return result;
	}

	public checkChanged(): void {
		this.enableDisable();
	}

	private enableDisable(): void {
		if (this.gwEnabled.value) {
			this.gwDomainNames.enable();
			this.gwQueryPageSize.enable();
			this.gwAutoSyncDirectoryDataEnabled.enable();
			if (this.gwAutoSyncDirectoryDataEnabled.value) {
				this.gwAutoSyncDirectoryDataDays.enable();
				this.gwAutoSyncDirectoryDataHours.enable();
				this.gwAutoSyncDirectoryDataMinutes.enable();
			}
			else {
				this.gwAutoSyncDirectoryDataDays.disable();
				this.gwAutoSyncDirectoryDataHours.disable();
				this.gwAutoSyncDirectoryDataMinutes.disable();
			}
		}
		else {
			this.gwDomainNames.disable();
			this.gwQueryPageSize.disable();
			this.gwAutoSyncDirectoryDataEnabled.disable();
			this.gwAutoSyncDirectoryDataDays.disable();
			this.gwAutoSyncDirectoryDataHours.disable();
			this.gwAutoSyncDirectoryDataMinutes.disable();
		}
	}

	public enableSave(): boolean {
		return (!!this.gwSettings && this.form.dirty && this.form.valid);
	}

	public onGwAdminDialogOkClicked(): void {

		this.getSettings();

		let finalRedirectUrl = location.origin + "/admin/authsettings/save/googleworkspace";
		let errorRedirectUrl = location.origin + "/admin/authsettings/loginadmin/failed";

		this.identityService.loginGoogleWorkspaceSetAuthSettingsAdmin(
			this.gwSettings, finalRedirectUrl, errorRedirectUrl).subscribe(
			(result: LoginResultDto) => {
				// Do nothing because a successful call will result in a redirect
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not initiate Google admin login");
			}
		);

		this.resetGwAdminDialogState();
	}

	public onGwAdminDialogCancelClicked(): void {
		this.resetGwAdminDialogState();
	}

	private static readonly Key_StashedGoogleWorkspaceSettings = "stashed-google-workspace-settings";

	public onSave(): void {
		if (this.gwEnabled.value) {
			this.getSettings();

			let serializedSettings = {};
			this.gwSettings.Write(serializedSettings, {});
			let serializedSettingsStr = JSON.stringify(serializedSettings);

			let entry = new SessionStateEntryDto();
			entry.Key = GwAuthSettingsComponent.Key_StashedGoogleWorkspaceSettings;
			entry.StringValue = serializedSettingsStr;

			this.identityService.setSessionStateEntry(entry).subscribe(
				() => {
					this.gwAdminDialogVisible = true;
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not stash unsaved settings");
				});
		}
		else {
			this.save();
		}
	}

	public onOauthSave(): void {
		this.runAfterAdminAuthActions(true);
	}

	public onAdminLoginFailed(): void {
		this.runAfterAdminAuthActions(false);
	}

	private runAfterAdminAuthActions(save: boolean): void {

		this.identityService.getSessionStateEntry(GwAuthSettingsComponent.Key_StashedGoogleWorkspaceSettings).subscribe(
			(entry: SessionStateEntryDto) => {

				let serializedSettingsStr = entry.StringValue;
				let serializedSettings = JSON.parse(serializedSettingsStr);

				this.gwSettings = new GoogleWorkspaceSettingsDto();
				this.gwSettings.Read(serializedSettings, {});

				this.buildForm();
				this.form.markAsDirty();

				if (save) {
					this.save();
				}

				this.identityService.deleteSessionStateEntry(GwAuthSettingsComponent.Key_StashedGoogleWorkspaceSettings).subscribe(
					() => { },
					(error) => {
						console.error("Could not delete unsaved settings");
					});
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not retrieve unsaved settings");
			});
	}

	private save(): void {
		this.getSettings();

		this.messageService.displayInfoMessage("Saving authentication settings");
		this.messageService.startWork();
		this.usersService.setAuthSettings(null, this.gwSettings, null).subscribe(
			(result: boolean) => {
				//this.usersService.dirGroupsCache.remove(AccountType.GoogleWorkspace);
				this.buildForm();
				this.messageService.endWork();
				this.messageService.displayInfoMessage("Authentication settings were saved");
			},
			(error) => {
				//this.usersService.dirGroupsCache.remove(AccountType.GoogleWorkspace);
				this.messageService.endWork();
				this.messageService.displayErrorMessage("Could not save authentication settings");
			});
	}

	private getSettings(): void {
		this.gwSettings.Enabled = this.gwEnabled.value;
		this.gwSettings.DomainNames = this.stringToDomainNames(this.gwDomainNames.value);
		this.gwSettings.QueryPageSize = this.gwQueryPageSize.value;
		this.gwSettings.AutoSyncDirectoryData_Enabled = this.gwAutoSyncDirectoryDataEnabled.value;
		this.gwSettings.AutoSyncDirectoryData_Days = this.gwAutoSyncDirectoryDataDays.value;
		this.gwSettings.AutoSyncDirectoryData_Hours = this.gwAutoSyncDirectoryDataHours.value;
		this.gwSettings.AutoSyncDirectoryData_Minutes = this.gwAutoSyncDirectoryDataMinutes.value;
	}

}
